class BentoHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-bento-campaign-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-bento-dot]'));
    this.currentIndex = 0;
    this.manualPaused = false;
    this.interactionPaused = false;
    this.editorPaused = false;
    this.pointerStartX = null;
    this.controller = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplaySpeed = Number.parseInt(this.dataset.autoplaySpeed, 10) || 6000;

    this.classList.add('is-ready');
    this.showSlide(0, false);
    if (this.slides.length < 2) return;

    this.tabIndex = 0;
    this.bindEvents();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    window.clearTimeout(this.holdTimer);
    this.controller?.abort();
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
  }

  bindEvents() {
    const options = { signal: this.controller.signal };

    this.addEventListener('click', (event) => {
      const target = event.target.closest('button');
      if (!target) return;
      if (target.hasAttribute('data-bento-next')) this.showSlide(this.currentIndex + 1);
      if (target.hasAttribute('data-bento-previous')) this.showSlide(this.currentIndex - 1);
      if (target.hasAttribute('data-bento-dot')) this.showSlide(Number(target.dataset.bentoDot));
      if (target.hasAttribute('data-bento-pause')) this.toggleAutoplay(target);
      if (target.hasAttribute('data-btn-addtocart')) this.holdAutoplay();
    }, options);

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-split-hero-variant]')) this.holdAutoplay();
    }, options);

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.showSlide(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);

    this.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('.bento-hero__campaign')) return;
      this.pointerStartX = event.clientX;
    }, options);

    this.addEventListener('pointerup', (event) => {
      if (this.pointerStartX === null) return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(distance) < 40) return;
      this.showSlide(this.currentIndex + (distance < 0 ? 1 : -1));
      this.holdAutoplay();
    }, options);

    this.addEventListener('mouseenter', () => this.pauseForInteraction(), options);
    this.addEventListener('mouseleave', () => this.resumeAfterInteraction(), options);
    this.addEventListener('focusin', () => this.pauseForInteraction(), options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resumeAfterInteraction();
    }, options);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }, options);
    this.reducedMotion.addEventListener('change', () => this.startAutoplay(), options);

    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-bento-campaign-slide]');
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.editorPaused = true;
      this.stopAutoplay();
      this.showSlide(index, false);
    }, options);
  }

  showSlide(index, animate = true) {
    if (!this.slides.length) return;
    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.toggleAttribute('data-animate', animate);

    for (const [slideIndex, slide] of this.slides.entries()) {
      const active = slideIndex === this.currentIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      for (const link of slide.querySelectorAll('a, button, [tabindex]')) {
        if (active) link.removeAttribute('tabindex');
        else link.tabIndex = -1;
      }
    }

    for (const [dotIndex, dot] of this.dots.entries()) {
      const isActive = dotIndex === this.currentIndex;
      const item = dot.closest('.dots-item');
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
        item?.classList.add('active');
      } else {
        dot.removeAttribute('aria-current');
        item?.classList.remove('active');
      }
    }
  }

  pauseForInteraction() {
    this.interactionPaused = true;
    this.stopAutoplay();
  }

  resumeAfterInteraction() {
    this.interactionPaused = false;
    this.startAutoplay();
  }

  holdAutoplay() {
    this.pauseForInteraction();
    window.clearTimeout(this.holdTimer);
    this.holdTimer = window.setTimeout(() => this.resumeAfterInteraction(), 8000);
  }

  toggleAutoplay(button) {
    this.manualPaused = !this.manualPaused;
    button.setAttribute('aria-pressed', String(this.manualPaused));
    if (this.manualPaused) this.stopAutoplay();
    else this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || this.manualPaused || this.interactionPaused || this.editorPaused || document.hidden) return;
    this.timer = window.setInterval(() => this.showSlide(this.currentIndex + 1), this.autoplaySpeed);
  }

  stopAutoplay() {
    window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('bento-hero')) {
  customElements.define('bento-hero', BentoHero);
}

export { BentoHero };
